import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { sitesV2Api } from '../../../api/sitesV2Api';
import config from '../../../config/config';
import './InviteAcceptPage.css';

const InviteAcceptPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invitationInfo, setInvitationInfo] = useState<{
    siteId: number;
    siteName: string;
    createdByName: string;
    expiresAt: string;
    isValid: boolean;
    targetExecutorName?: string | null;
  } | null>(null);

  useEffect(() => {
    if (token) {
      loadInvitationInfo();
    }
  }, [token]);

  const loadInvitationInfo = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const info = await sitesV2Api.getInvitationInfo(token);
      setInvitationInfo(info);
    } catch (err: any) {
      console.error('Ошибка загрузки информации о приглашении:', err);
      setError(err.response?.data?.message || err.message || 'Приглашение не найдено');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!token || !isAuthenticated) {
      // Перенаправляем на страницу входа с токеном в state
      navigate(config.app.loginRoute, { 
        state: { from: { pathname: `/invite/${token}` } } 
      });
      return;
    }

    try {
      setAccepting(true);
      setError(null);
      
      await sitesV2Api.acceptInvitation(token);
      
      // Перенаправляем на объект
      if (invitationInfo) {
        navigate(`/dashboard?siteId=${invitationInfo.siteId}`);
      } else {
        navigate('/dashboard');
      }
    } catch (err: any) {
      console.error('Ошибка принятия приглашения:', err);
      setError(err.response?.data?.message || err.message || 'Ошибка принятия приглашения');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="invite-accept-page">
        <div className="invite-accept-container">
          <div className="loading-state">
            <i className="ri-loader-4-line ri-spin"></i>
            <span>Загрузка информации о приглашении...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !invitationInfo) {
    return (
      <div className="invite-accept-page">
        <div className="invite-accept-container">
          <div className="error-state">
            <i className="ri-error-warning-line"></i>
            <h2>Ошибка</h2>
            <p>{error || 'Приглашение не найдено'}</p>
            <button 
              className="action-button primary"
              onClick={() => navigate('/')}
            >
              Вернуться на главную
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!invitationInfo.isValid) {
    return (
      <div className="invite-accept-page">
        <div className="invite-accept-container">
          <div className="invalid-state">
            <i className="ri-time-line"></i>
            <h2>Приглашение недействительно</h2>
            <p>Это приглашение уже использовано или срок его действия истёк.</p>
            <button 
              className="action-button primary"
              onClick={() => navigate('/')}
            >
              Вернуться на главную
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="invite-accept-page">
      <div className="invite-accept-container">
        <div className="invite-accept-content">
          <div className="invite-header">
            <div className="invite-icon">
              <i className="ri-team-line"></i>
            </div>
            <h1>Приглашение в объект</h1>
          </div>

          <div className="invite-info-card">
            <div className="invite-info-item">
              <div className="invite-info-label">
                <i className="ri-building-line"></i>
                <span>Объект</span>
              </div>
              <div className="invite-info-value">
                {invitationInfo.siteName}
              </div>
            </div>

            <div className="invite-info-item">
              <div className="invite-info-label">
                <i className="ri-user-line"></i>
                <span>Вас пригласил</span>
              </div>
              <div className="invite-info-value">
                {invitationInfo.createdByName}
              </div>
            </div>

            {invitationInfo.targetExecutorName && (
              <div className="invite-info-item">
                <div className="invite-info-label">
                  <i className="ri-user-received-line"></i>
                  <span>Вы вступите как</span>
                </div>
                <div className="invite-info-value">
                  {invitationInfo.targetExecutorName}
                </div>
              </div>
            )}

            <div className="invite-info-item">
              <div className="invite-info-label">
                <i className="ri-time-line"></i>
                <span>Действительно до</span>
              </div>
              <div className="invite-info-value">
                {new Date(invitationInfo.expiresAt).toLocaleString('ru-RU')}
              </div>
            </div>
          </div>

          {invitationInfo.targetExecutorName && (
            <div className="invite-target-note">
              <i className="ri-information-line"></i>
              <span>
                К вам перейдут записи исполнителя «{invitationInfo.targetExecutorName}»: смены, начисления и ставка.
              </span>
            </div>
          )}

          {error && (
            <div className="error-message">
              <i className="ri-error-warning-line"></i>
              <span>{error}</span>
            </div>
          )}

          <div className="invite-actions">
            {isAuthenticated ? (
              <button
                className="action-button primary large"
                onClick={handleAccept}
                disabled={accepting}
              >
                {accepting ? (
                  <>
                    <i className="ri-loader-4-line ri-spin"></i>
                    Вступление...
                  </>
                ) : (
                  <>
                    <i className="ri-check-line"></i>
                    Вступить в объект
                  </>
                )}
              </button>
            ) : (
              <button
                className="action-button primary large"
                onClick={handleAccept}
              >
                <i className="ri-login-box-line"></i>
                Войти, чтобы вступить
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteAcceptPage;
